/*****************************************************
Naam: useSocketConnection.js
Locatie: src/components/Dashboard/hooks/useSocketConnection.js
Datum: 2026-02-25
Versie: 2.0.0
******************************************************
Beschrijving:
Custom hook voor Socket.IO verbinding.

CHANGELOG v2.0.0 (2026-02-25):
- NIEUW: aanvraag-created listener → refresh counts
- NIEUW: aanvraag-updated listener → refresh counts
- NIEUW: deadline-verlopen listener → refresh deadline count
- Accepteert onRefreshCounts en onDeadlineUpdate callbacks
- Gesynchroniseerd met monoliet Dashboard.jsx
******************************************************/

import React, { useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';

const getSocketURL = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  if (hostname === 'dashboard.sendwell.nl' || hostname.includes('sendwell')) {
    return 'https://dashboard.sendwell.nl';
  }
  
  return window.location.origin;
};

export const useSocketConnection = (onEmailQueueUpdate, onRefreshCounts, onDeadlineUpdate, onNieuwKlantBericht, onGeofenceEvent) => {

  // Ref zodat de socket listener altijd de nieuwste callback gebruikt
  // zonder de listener opnieuw te registreren
  const onGeofenceEventRef = React.useRef(onGeofenceEvent);
  React.useEffect(() => {
    onGeofenceEventRef.current = onGeofenceEvent;
  }, [onGeofenceEvent]);
  
  const initSocket = useCallback(async () => {
    try {
      const res = await fetch('/api/user-info');
      const data = await res.json();
      
      if (!data.success) return;
      
      // Initialize socket globally on window object
      if (!window.socket || !window.socket.connected) {
        const socketURL = getSocketURL();
        
        window.socket = io(socketURL, {
          transports: ['websocket', 'polling'],
          secure: socketURL.startsWith('https')
        });
        
      }
      
      const socket = window.socket;
      
      socket.on('connect', () => {
        socket.emit('start-email-monitor', { userType: data.userType });
      });

      socket.on('email-notification', (notification) => {
      });
      
      socket.on('email-queue-updated', () => {
        if (onEmailQueueUpdate) {
          onEmailQueueUpdate();
        }
      });
      
      socket.on('email-monitor-error', (error) => {
        console.error('❌ Email monitor error:', error.message);
      });

      socket.on('disconnect', () => {
      });
      
      // Luister naar opdracht ontvangen van klanten portal
      socket.on('opdracht-ontvangen', (eventData) => {
        window.dispatchEvent(new CustomEvent('refresh-opdracht-count'));
      });

      // ⚡ Luister naar nieuwe aanvragen (portal/email)
      socket.on('aanvraag-created', (eventData) => {
        window.dispatchEvent(new CustomEvent('refresh-aanvragen', { detail: eventData }));
        if (onRefreshCounts) onRefreshCounts();
      });

      // ⚡ Luister naar gewijzigde aanvragen (status update)
      socket.on('aanvraag-updated', (eventData) => {
        if (onRefreshCounts) onRefreshCounts();
      });

      // ⏰ Luister naar verlopen deadlines
      socket.on('deadline-verlopen', (eventData) => {
        if (onDeadlineUpdate) onDeadlineUpdate();
        if (onRefreshCounts) onRefreshCounts();
      });

      // 💬 Luister naar nieuwe klantberichten
      socket.on('nieuw-klant-bericht', (eventData) => {
        if (onNieuwKlantBericht) onNieuwKlantBericht();
      });

      // 📍 Luister naar GEOfencing hub passages (via ref — altijd actuele callback)
      socket.off('geofence-event'); // verwijder eventuele oude listener
      socket.on('geofence-event', (eventData) => {
        if (onGeofenceEventRef.current) onGeofenceEventRef.current(eventData);
      });
      
    } catch (err) {
      console.error('❌ Failed to init socket:', err);
    }
  }, [onEmailQueueUpdate, onRefreshCounts, onDeadlineUpdate]);

  useEffect(() => {
    let mounted = true;
    
    if (mounted) {
      initSocket();
    }
    
    return () => {
      mounted = false;
      // Don't disconnect - keep socket alive for EmailCheckProgress
    };
  }, [initSocket]);

  return {
    socket: window.socket
  };
};

export default useSocketConnection;
